import { rotateAnimation } from '@accredible-frontend-v2/animations';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  ViewEncapsulation,
} from '@angular/core';

export type ButtonSize = 'small' | 'normal' | 'large';
export type ButtonColor = 'primary' | 'secondary' | 'natural' | 'success' | 'warning' | 'danger';
export type ButtonType = 'solid' | 'outline' | 'flat' | 'icon';
export type IconPosition = 'left' | 'right';

@Component({
  selector: `button[accredible-button], button[accredible-button-flat], button[accredible-button-outline], button[accredible-button-icon],
    a[accredible-button], a[accredible-button-flat], a[accredible-button-outline], a[accredible-button-icon]`,
  templateUrl: './button.component.html',
  styleUrls: [
    './button.component.scss',
    './button-solid.component.scss',
    './button-outline.component.scss',
    './button-flat.component.scss',
    './button-icon.component.scss',
  ],
  animations: [rotateAnimation],
  // ViewEncapsulation.None in order to be able to target the HTML tags <a> and <button> specifically
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'size() + " " + color()',
    '[attr.aria-disabled]': 'disabled',
  },
})
export class AccredibleButtonComponent {
  private readonly _liveAnnouncer = inject(LiveAnnouncer);
  private readonly _language = inject(AccredibleLanguageService);
  // ElementRef is not private because is used to calculate rotateIcon using the ariaExpanded property of the nativeElement
  readonly el = inject(ElementRef);

  size = input<ButtonSize>('normal');
  color = input<ButtonColor>('primary');
  icon = input<string>();
  iconSize = input<string>();
  iconColor = input<string>();
  iconPosition = input<IconPosition>('right');
  rotateIcon = model<boolean>(false);
  isLoading = input<boolean>(false);
  hideText = input<boolean>(false);
  loadingAnnouncement = input<string>();

  constructor() {
    effect(() => {
      if (this.isLoading()) {
        this._liveAnnouncer.announce(
          this.loadingAnnouncement() || this._language.translate('button.loading'),
        );
      }
    });
  }

  // TODO(Manuel): ariaExpanded state is not handled correctly in onPush components, this will be fixed in angular 15.
  // Remove this hostListener when angular is updated
  // TODO(Fred): UPDATE: The comment above is not valid, I tested removing this hostlistener and the issue still exists.
  // I think the solution to rotate the icon should not rely on ariaExpanded property but rather on the actual events from the cdkMenu.
  @HostListener('cdkMenuOpened')
  @HostListener('cdkMenuClosed')
  setAriaExpanded(): void {
    this.el.nativeElement.ariaExpanded =
      this.el.nativeElement.ariaExpanded === 'true' ? 'false' : 'true';
  }
}
